// Pre-W3b usability fix (2026-02) · Bounded autocomplete over the
// combined Client + Organization display-name index. Wraps a plain
// text <input>, exposes a small suggestions list on focus/typing, and
// invokes onChange with the raw string (whether typed or picked). No
// relationship side-effects: this component only writes to the text
// field the parent form controls.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";

const DEBOUNCE_MS = 180;

export function ClientOrgAutocomplete({
  value,
  onChange,
  placeholder,
  testid,
  minChars = 1,
  limit = 15,
}) {
  const [focused, setFocused] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);

  const query = useMemo(() => (value || "").trim(), [value]);
  const open = focused && query.length >= minChars && results.length > 0;

  useEffect(() => {
    if (!focused) return;
    if (query.length < minChars) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/lookups/client-org", {
          params: { q: query, limit },
        });
        if (!cancelled) {
          setResults(r.data?.results || []);
          setHighlight(-1);
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, focused, limit, minChars]);

  // Close on outside click.
  useEffect(() => {
    if (!focused) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [focused]);

  const pick = (row) => {
    onChange(row.name);
    setFocused(false);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(results.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      pick(results[highlight]);
    } else if (e.key === "Escape") {
      setFocused(false);
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <input
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
      />
      {open && (
        <ul
          data-testid={`${testid}-suggestions`}
          className="absolute z-40 left-0 right-0 mt-1 max-h-56 overflow-auto bg-white border border-coach-silver rounded-sm shadow-md"
        >
          {loading && results.length === 0 && (
            <li className="px-2.5 py-1.5 text-[12px] text-steel">Searching…</li>
          )}
          {results.map((r, i) => (
            <li
              key={`${r.source_type}:${r.id}`}
              data-testid={`${testid}-suggestion-${r.source_type}-${r.id}`}
              onMouseDown={(e) => {
                // Use mouseDown so blur doesn't fire before pick.
                e.preventDefault();
                pick(r);
              }}
              onMouseEnter={() => setHighlight(i)}
              className={`px-2.5 py-1.5 text-[12.5px] cursor-pointer flex items-center justify-between gap-3 ${
                i === highlight ? "bg-coach-blue/10" : "hover:bg-road-white"
              }`}
            >
              <span className="truncate text-carbon">{r.name}</span>
              <span
                className={`shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm ${
                  r.source_type === "client"
                    ? "bg-coach-blue/10 text-coach-blue"
                    : "bg-steel/10 text-steel"
                }`}
              >
                {r.source_type === "client" ? "Client" : "Organization"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default ClientOrgAutocomplete;
